<template>
  <div>    
    <div class="max-w-xl p-6 mx-auto">
      <BookHelpBubble 
        v-if="options.date_help_text" 
        :content="options.date_help_text" />
      
      <DatePicker 
        class="datepicker" 
        is-inline 
        is-expanded 
        mode="date" 
        v-model="form.date" 
        :min-date="minDate"
        :max-date="maxDate" 
        :disabled-dates="disabledDates"
        @input="onDateChange" />
        
      <validations :errors="errors" field="date" />
    </div>

    <BookBottomBar>
      <template slot="back">
        <button class="w-full btn btn-outline" @click="$parent.back()">
          <fa :icon="['far', 'chevron-left']" class="mr-2 text-sm" /> Back
        </button>
      </template>
      <template slot="next">
        <button class="w-full btn btn-accent" :class="{ 'opacity-75 cursor-not-allowed': options.date_requirement === 'required' && !form.date }" @click="next()">
          Next <fa :icon="['far', 'chevron-right']" class="ml-2 text-sm" />
        </button>
      </template>
    </BookBottomBar>
    
  </div>
</template>

<script>
  import { addDays, addHours } from 'date-fns'
  import { mapFields } from 'vuex-map-fields'
  import BookHelpBubble from './BookHelpBubble'
  import BookBottomBar from './BookBottomBar'
  import DatePicker from 'v-calendar/lib/components/date-picker.umd'

  export default {
    name: 'BookDate',

    components: {
      BookHelpBubble,
      BookBottomBar,
      DatePicker
    },

    computed: {
      ...mapFields('booking', ['form', 'options']),

      minDate () {
        return addHours(new Date(), this.options.minimum_lead_time || 0)
      },

      maxDate () {
        return addDays(new Date(), this.options.maximum_lead_time || 365)
      },

      /**
       * We check if any schedule applied to this booking form has a weekday that is active
       * Then we run through this composite schedule to set the disabled days of the week
       * i.e. the days which don't appear in any of the attached schedules
       */
      disabledDates () {
        const weekdays = [1,2,3,4,5,6,7]
        
        const anySchedule = {
          sunday: false,
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
        }

        for (let schedule of this.options.schedules) {
          if (schedule.sunday)    { anySchedule.sunday = true }
          if (schedule.monday)    { anySchedule.monday = true }
          if (schedule.tuesday)   { anySchedule.tuesday = true }
          if (schedule.wednesday) { anySchedule.wednesday = true }
          if (schedule.thursday)  { anySchedule.thursday = true }
          if (schedule.friday)    { anySchedule.friday = true }
          if (schedule.saturday)  { anySchedule.saturday = true }
        }

        if (anySchedule.sunday)    { weekdays.splice(weekdays.findIndex(n => n === 1), 1) }
        if (anySchedule.monday)    { weekdays.splice(weekdays.findIndex(n => n === 2), 1) }
        if (anySchedule.tuesday)   { weekdays.splice(weekdays.findIndex(n => n === 3), 1) }
        if (anySchedule.wednesday) { weekdays.splice(weekdays.findIndex(n => n === 4), 1) }
        if (anySchedule.thursday)  { weekdays.splice(weekdays.findIndex(n => n === 5), 1) }
        if (anySchedule.friday)    { weekdays.splice(weekdays.findIndex(n => n === 6), 1) }
        if (anySchedule.saturday)  { weekdays.splice(weekdays.findIndex(n => n === 7), 1) }

        /**
         * South African Public Holidays
         * TODO: Figure out a way to make this dynamic
         * NOTE: Months are 0 indexed (FFS)
         */
        const holidays = [
          new Date(2021, 3, 2),
          new Date(2021, 3, 5),
          new Date(2021, 3, 27),
          new Date(2021, 5, 16),
          new Date(2021, 7, 9),
          new Date(2021, 8, 24),
          new Date(2021, 11, 16),
          new Date(2021, 11, 25),
          new Date(2021, 11, 27),
        ]

        return [{ weekdays }, ...holidays]
      }
    },

    data () {
      return {
        errors: [],
        config: {
          mode: 'single'
        }
      }
    },

    methods: {
      onDateChange (val) {
        this.form.date.setHours(12)
      },

      next () {
        if (this.options.date_requirement === 'required' && !this.form.date) {
          this.$toasted.error('Please select a date to proceed.')
          this.errors = [
            { 
              field: 'date', 
              rule: 'required', 
              message: 'Please select a date to proceed.' 
            }
          ]
        } else {
          this.$parent.next()
        }
      }
    }
  }
</script>

<style lang="postcss">
  .datepicker {
    @apply h-full p-2 max-w-full;

    --day-content-width:2.5rem !important;
    --day-content-height:2.5rem !important;
    --weekday-padding:1rem !important;
    --arrows-padding:.5rem 0.75rem !important;
    --highlight-height:2.5rem !important;

    @screen sm {
      --weekday-padding:1.5rem !important;
    }

    &.vc-border { 
      @apply border-transparent;
    }

    .vc-title-layout {
      @apply items-center;
    }

    .vc-weekday {
      @apply py-4;
    }

    .vc-day {
      @apply h-12;
    }

    .vc-day-content,
    .vc-highlight {
      @apply h-10 w-10;
    }
  }
</style>